<template>
  <b-form-group label="บัตรกำนัล">
    <template v-if="!hasApplied">
      <b-input-group>
        <b-form-input
          ref="codeField"
          v-model="typed"
          :disabled="disabled || busy"
          placeholder="สแกนหรือพิมพ์รหัสบัตร"
          autocomplete="off"
          @keydown.enter.prevent="onTypedSubmit"
        />
        <template #append>
          <b-button
            variant="outline-secondary"
            :disabled="disabled || busy"
            title="สแกนด้วยกล้อง"
            @click="$emit('open-camera')"
          >
            <i class="fas fa-camera"></i>
          </b-button>
          <b-button
            variant="primary"
            :disabled="disabled || busy || !typed"
            @click="onTypedSubmit"
          >
            ใช้บัตร
            <b-spinner v-if="busy" small></b-spinner>
          </b-button>
        </template>
      </b-input-group>
      <b-form-invalid-feedback :state="!localError" force-show>
        {{ localError }}
      </b-form-invalid-feedback>
      <small class="text-muted">สแกนได้เลยโดยไม่ต้องคลิกช่องนี้</small>
    </template>
    <template v-else>
      <div class="d-flex align-items-center justify-content-between">
        <span class="text-success">ใช้บัตรแล้ว: {{ displayApplied }}</span>
        <b-button size="sm" variant="outline-danger" @click="clearApplied">
          เอาบัตรออก
        </b-button>
      </div>
    </template>
  </b-form-group>
</template>

<script>
import { normalizeCode, isValidChecksum } from "@/utils/voucherCodec";
import { ScanBuffer, charFromCode } from "./scanBuffer";

export default {
  name: "VoucherCodeInput",
  props: {
    disabled: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
    appliedCode: { type: String, default: null },
  },
  data() {
    return {
      typed: "",
      localError: "",
    };
  },
  computed: {
    hasApplied() { return !!this.appliedCode; },
    displayApplied() {
      if (!this.appliedCode) return "";
      // The prefix length is not knowable client-side, so show the code as the
      // server stores it — uppercase, undashed. formatCode is used only where a
      // prefix length is genuinely known (the export files).
      return this.appliedCode;
    },
  },
  methods: {
    // Shared by typed entry, HID scan, and camera decode. Every path lands here,
    // so the checksum gate cannot be bypassed by adding another input mode later.
    accept(raw) {
      const normalized = normalizeCode(raw);
      if (!normalized) {
        this.localError = "กรุณากรอกรหัสบัตรกำนัล";
        return;
      }
      if (!isValidChecksum(normalized)) {
        // Rejected before any round trip: the check character catches every
        // single-character typo and every adjacent transposition.
        this.localError = "รหัสบัตรไม่ถูกต้อง กรุณาตรวจสอบอีกครั้ง";
        return;
      }
      this.localError = "";
      this.typed = "";
      this.$emit("submit", normalized);
    },

    onTypedSubmit() {
      this.accept(this.typed);
    },

    clearApplied() {
      this.typed = "";
      this.localError = "";
      this.$emit("clear");
    },

    // Document-level so a scan lands even when focus is on the receipt table.
    onDocumentKeydown(event) {
      if (this.disabled || this.busy || this.hasApplied) return;

      // If the operator is typing into some other field, that field owns the
      // keystroke. Our own input is exempt — it routes through onTypedSubmit.
      // $refs.codeField is a b-form-input component instance, not a DOM node,
      // so the comparison is against its underlying element.
      const target = event.target;
      const tag = target && target.tagName;
      const codeFieldEl = this.$refs.codeField && this.$refs.codeField.$el;
      const inOtherField =
        (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || (target && target.isContentEditable)) &&
        target !== codeFieldEl;
      if (inOtherField) return;

      // NumpadEnter too: many HID scanners are configured to terminate with it.
      if (event.code === "Enter" || event.code === "NumpadEnter") {
        const payload = this.scanBuffer.takeIfScan(event.timeStamp);
        if (payload) {
          event.preventDefault();
          // stopPropagation matters as much as preventDefault. This listener is
          // capture-phase on document, so it runs BEFORE the input's own handler.
          // Without it, a scan landing while focus is in our field is accepted
          // here AND then re-submitted by the field with the now-cleared value.
          event.stopPropagation();
          this.accept(payload);
        }
        // Not a scan: a human typed it. Let the event reach the field's own
        // Enter handler, which submits whatever they typed.
        return;
      }
      const char = charFromCode(event.code);
      if (char) this.scanBuffer.push(char, event.timeStamp);
    },
  },
  created() {
    // NOT in data(): Vue 2 would deep-walk it and make chars/lastTs reactive,
    // adding setter overhead on every buffered keystroke for state that is
    // deliberately Vue-agnostic.
    this.scanBuffer = new ScanBuffer();
  },
  mounted() {
    document.addEventListener("keydown", this.onDocumentKeydown, true);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onDocumentKeydown, true);
    this.scanBuffer.reset();
  },
};
</script>
